// SpeakerProfileForm.js - FINAL COMPLETE PROFESSIONAL VERSION
import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import Topics from "./topic";
import CountrySelect from "./CountrySelect";
import "./style.scss";

const SpeakerProfileForm = () => {
  const BaseUrl = "https://eventscons.com/eventbackendnew/api";
  const storageBase = "https://eventscons.com/eventbackendnew/storage/app/public/";
  const token = localStorage.getItem("token");

  // Existing file paths (strings)
  const [abstractExisting, setAbstractExisting] = useState("");
  const [secondAbstractExisting, setSecondAbstractExisting] = useState("");
  const [videoExisting, setVideoExisting] = useState("");
  const [secondVideoExisting, setSecondVideoExisting] = useState("");
  const [presentationExisting, setPresentationExisting] = useState("");
  const [secondPresentationExisting, setSecondPresentationExisting] = useState("");

  // Newly selected files (File objects)
  const [abstractFile, setAbstractFile] = useState(null);
  const [secondAbstractFile, setSecondAbstractFile] = useState(null);
  const [videoFile, setVideoFile] = useState(null);
  const [secondVideoFile, setSecondVideoFile] = useState(null);
  const [presentationFile, setPresentationFile] = useState(null);
  const [secondPresentationFile, setSecondPresentationFile] = useState(null);

  // Profile fields
  const [displayName, setDisplayName] = useState("");
  const [userTitle, setuserTitle] = useState("");
  const [userImage, setUserImage] = useState(null);
  const [userImageUrl, setUserImageUrl] = useState("");
  const [userImagePath, setUserImagePath] = useState("");
  const [bio, setBio] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [whatsAppNumber, setWhatsAppNumber] = useState("");
  const [country, setCountry] = useState("");
  const [nationality, setNationality] = useState("");
  const [arrivalDate, setArrivalDate] = useState("");
  const [departureDate, setDepartureDate] = useState("");
  const [topics, setTopics] = useState([]);
  const [conferenceId, setConferenceId] = useState(null);
  const [speakerId, setSpeakerId] = useState(null);
  const [onlineParticipation, setOnlineParticipation] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  // Additional flags
  const [onlineApproved, setOnlineApproved] = useState(false);
  const [certificateActive, setCertificateActive] = useState(false);
  const [airportPickup, setAirportPickup] = useState(false);
  const [visaPaymentRequired, setVisaPaymentRequired] = useState(false);

  const safe = (v) => (v === undefined || v === null ? "" : String(v));

  const formatDateForBackend = (dateVal) => {
    const normalized = dateVal ? String(dateVal).trim() : "";
    return normalized && normalized !== "" ? normalized : "null";
  };

  const nameOnly = (fullName = "", title = "") => {
    const n = String(fullName || "").trim();
    const t = String(title || "").trim();
    if (!n) return "";
    if (!t) return n;
    const tEsc = t.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const re = new RegExp(`^${tEsc}\\.?\\s+`, "i");
    return n.replace(re, "").trim();
  };

  const makeDisplayName = (title, rawName) => {
    const t = String(title || "").trim();
    const clean = nameOnly(rawName, t);
    if (!t) return clean;
    if (!clean) return t;
    return `${t} ${clean}`.trim();
  };

  const fileNameFromPath = (p = "") => {
    if (!p) return "";
    try {
      const url = new URL(p, storageBase);
      const pathname = url.pathname;
      return pathname.split("/").pop() || "";
    } catch {
      return String(p).split("/").pop() || String(p);
    }
  };

  const openExisting = (p) => {
    if (!p) return;
    const url = p.startsWith("http") ? p : storageBase + p;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const fetchSpeakerInfo = useCallback(async () => {
    if (!token) {
      alert("Token not found");
      return;
    }
    try {
      const res = await axios.get(`${BaseUrl}/speakers/info`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const speaker = res.data.speaker;
      if (!speaker) {
        alert("Speaker data not found");
        return;
      }

      const titleIncoming = speaker.user_title || speaker.title || "";
      const nameIncoming = speaker.user_name || speaker.name || "";
      setuserTitle(titleIncoming);
      setDisplayName(makeDisplayName(nameIncoming));

      const imgPath = speaker.user_image || speaker.image || "";
      setUserImagePath(imgPath);
      setUserImageUrl(imgPath ? `${storageBase}${imgPath}` : "");

      setBio(speaker.biography || "");
      setPhoneNumber(speaker.phone_number || "");
      setWhatsAppNumber(speaker.whatsapp_number || "");
      setCountry(speaker.country_of_residence || "");
      setNationality(speaker.nationality || "");
      
      setArrivalDate(speaker.arrival_date || "");
      setDepartureDate(speaker.departure_date || "");
      
      setConferenceId(speaker.conference_id || null);
      setSpeakerId(speaker.id || null);
      setOnlineParticipation(Number(speaker.online_participation || 0));

      setAbstractExisting(speaker.abstract || "");
      setSecondAbstractExisting(speaker.second_abstract || "");
      setVideoExisting(speaker.video || "");
      setSecondVideoExisting(speaker.second_video || "");
      setPresentationExisting(speaker.presentation_file || "");
      setSecondPresentationExisting(speaker.second_presentation_file || "");

      setAbstractFile(null);
      setSecondAbstractFile(null);
      setVideoFile(null);
      setSecondVideoFile(null);
      setPresentationFile(null);
      setSecondPresentationFile(null);
      setUserImage(null);

      setOnlineApproved(Boolean(Number(speaker.is_online_approved || 0)));
      setCertificateActive(Boolean(Number(speaker.is_certificate_active || 0)));
      setAirportPickup(Boolean(Number(speaker.airport_pickup || 0)));
      setVisaPaymentRequired(speaker.visa_fees?.trim().toLowerCase() === "covered");
    } catch (error) {
      console.error("Failed to fetch speaker info:", error);
      alert("Failed to fetch speaker info");
    }
  }, [BaseUrl, storageBase, token]);

  useEffect(() => {
    fetchSpeakerInfo();
  }, [fetchSpeakerInfo]);

  const handleImageChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setUserImage(file);
      setUserImageUrl(URL.createObjectURL(file));
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    if (!conferenceId || !speakerId) {
      alert("Conference ID or Speaker ID missing");
      return;
    }
    if (!token) {
      alert("Token not found");
      return;
    }
    setIsLoading(true);

    const formData = new FormData();

    formData.append("_method", "POST");
    formData.append("conference_id", safe(conferenceId));
    formData.append("speaker_id", safe(speakerId));
    formData.append("online_participation", safe(Number(onlineParticipation)));
    
    formData.append("name", safe(displayName));
    formData.append("user_name", safe(displayName));
    formData.append("title", safe(userTitle));

    formData.append("biography", safe(bio));
    formData.append("phone_number", safe(phoneNumber));
    formData.append("whatsapp_number", safe(whatsAppNumber));
    formData.append("country_of_residence", safe(country));
    formData.append("nationality", safe(nationality));

    formData.append("arrival_date", formatDateForBackend(arrivalDate));
    formData.append("departure_date", formatDateForBackend(departureDate));

    if (userImage) {
      formData.append("image", userImage);
      formData.append("image_replace", "1");
    } else {
      formData.append("image_keep", "1");
      formData.append("image_path", safe(userImagePath));
    }

    if (presentationFile) formData.append("presentation_file", presentationFile);
    if (abstractFile) formData.append("abstract", abstractFile);
    if (secondAbstractFile) formData.append("second_abstract", secondAbstractFile);
    if (secondPresentationFile) formData.append("second_presentation_file", secondPresentationFile);
    if (secondVideoFile) formData.append("second_video", secondVideoFile);
    if (videoFile) formData.append("video", videoFile);

    const cleanedTopics = (topics || []).map((t) => (t == null ? "" : String(t)));
    cleanedTopics.forEach((t, idx) => formData.append(`topics[${idx}]`, t));

    try {
      const res = await axios.post(`${BaseUrl}/speakers/user/update`, formData, {
        headers: { Authorization: `Bearer ${token}` },
        transformRequest: [(data) => data],
      });
      alert("Profile updated successfully!");
      await fetchSpeakerInfo();
      console.log("Update response:", res.data);
    } catch (error) {
      console.error("Update failed:", error?.response?.data || error.message);
      alert(
        error?.response?.data?.message ||
        "Failed to update profile, please verify required fields and file types"
      );
    } finally {
      setIsLoading(false);
    }
  };

  const UploadRow = ({
    label,
    inputId,
    icon = "📁",
    accept,
    file,
    setFile,
    existing,
    clearExisting,
  }) => {
    const title = file?.name || fileNameFromPath(existing) || "No file";
    return (
      <div style={{ marginTop: 10 }}>
        <label style={labelStyle}>{label}</label>
        <div style={rowWrap}>
          <div style={growArea}>
            {file || existing ? (
              <button
                type="button"
                style={valueChip}
                title={title}
                onClick={() => openExisting(existing)}
                disabled={!existing}
              >
                {file?.name || fileNameFromPath(existing)}
              </button>
            ) : (
              <div style={placeholderChip}>No file selected</div>
            )}
            <input
              id={inputId}
              type="file"
              accept={accept}
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              style={{ display: "none" }}
            />
          </div>
          <label htmlFor={inputId} style={iconButton} title="Upload">
            <span style={{ fontSize: 18 }}>{icon}</span>
          </label>
          {(file || existing) && (
            <button type="button" onClick={clearExisting} style={clearBtn} title="Clear">
              ✖
            </button>
          )}
        </div>
      </div>
    );
  };

  return (
    <div style={containerStyle}>
      <form onSubmit={handleUpdate} style={formStyle}>
        {/* 🔥 FINAL PROFESSIONAL TITLE DROPDOWN + NAME HEADER */}
        <div style={profileHeader}>
          <div style={profileCover} />
          <div style={profileHeaderInner}>
            <div style={avatarWrap}>
              <div style={avatarRing}>
                {userImageUrl ? (
                  <img
                    src={userImageUrl.startsWith("blob:") ? userImageUrl : `${userImageUrl}`}
                    alt="User"
                    style={avatarImg}
                  />
                ) : (
                  <div style={avatarPlaceholder}>Upload</div>
                )}
              </div>
              <label htmlFor="image-upload" style={avatarEditBtn} title="Change photo">
                ✏️
              </label>
              <input
                type="file"
                id="image-upload"
                accept="image/*"
                onChange={handleImageChange}
                style={{ display: "none" }}
              />
            </div>

            <div style={profileText}>
              <div style={titleNameRow}>
                {/* Title Dropdown */}
                <div style={titleDropdownWrap}>
                  <select 
                    value={userTitle} 
                    onChange={(e) => setuserTitle(e.target.value)}
                    style={titleDropdown}
                  >
                    <option value="">Select Title</option>
                    <option value="Mr">Mr</option>
                    <option value="Mrs">Mrs</option>
                    <option value="Ms">Ms</option>
                    <option value="Dr">Dr</option>
                    <option value="Prof">Prof</option>
                    <option value="Eng">Eng</option>
                    <option value="Adv">Adv</option>
                  </select>
                </div>

                {/* Name Input (user_name) */}
                <div style={nameInputWrap}>
                  <input
                    type="text"
                    value={displayName}
                    onChange={(e) => setDisplayName(e.target.value)}
                    placeholder="Full Name"
                    style={nameInput}
                  />
                </div>
              </div>
              {/* <div style={helperLine}>
                Title from dropdown | Name editable (user_name parameter)
              </div> */}
            </div>
          </div>
        </div>

        {/* Biography */}
        <div style={{ marginTop: 20 }}>
          <label style={labelStyle}>
            Biography <span style={requiredStyle}>*</span>
          </label>
          <textarea
            value={bio}
            onChange={(e) => {
              const inputText = e.target.value;
              if (inputText.length <= 500) setBio(inputText);
            }}
            placeholder="Short biography (max 500 letters)"
            style={textareaStyle}
            required
          />
          <div style={{ fontSize: "12px", color: "gray", marginTop: "5px" }}>
            {bio.length}/500 letters
          </div>
        </div>

        {/* Phone */}
        <div style={{ marginTop: 10 }}>
          <label style={labelStyle}>
            Phone Number<span style={requiredStyle}> *</span>
          </label>
          <div style={rowWrap}>
            <input
              type="text"
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value)}
              placeholder="Enter phone number"
              style={inputStyle}
            />
          </div>
        </div>

        {/* WhatsApp */}
        <div style={{ marginTop: 10 }}>
          <label style={labelStyle}>
            WhatsApp Number<span style={requiredStyle}> *</span>
          </label>
          <div style={rowWrap}>
            <input
              type="text"
              value={whatsAppNumber}
              onChange={(e) => setWhatsAppNumber(e.target.value)}
              placeholder="Enter WhatsApp number"
              style={inputStyle}
            />
          </div>
        </div>

        {/* Country */}
        <div style={{ marginTop: 10 }}>
          <label style={labelStyle}>
            Country<span style={requiredStyle}> *</span>
          </label>
          <div style={rowWrap}>
            <CountrySelect
              value={country}
              onChange={(val) => setCountry(val)}
              placeholder="Select country"
            />
          </div>
        </div>

        {/* Nationality */}
        <div style={{ marginTop: 10 }}>
          <label style={labelStyle}>
            Nationality <span style={requiredStyle}>*</span>
          </label>
          <div style={rowWrap}>
            <input
              type="text"
              value={nationality}
              onChange={(e) => setNationality(e.target.value)}
              placeholder="Nationality"
              style={inputStyle}
              required
            />
          </div>
        </div>

        {/* Upload Rows */}
        <UploadRow
          label="Abstract 1"
          inputId="abstract1-input"
          icon="📁"
          accept=".pdf,.doc,.docx"
          file={abstractFile}
          setFile={setAbstractFile}
          existing={abstractExisting}
          clearExisting={() => {
            setAbstractExisting("");
            setAbstractFile(null);
          }}
        />
        <UploadRow
          label="Abstract 2"
          inputId="abstract2-input"
          icon="📁"
          accept=".pdf,.doc,.docx"
          file={secondAbstractFile}
          setFile={setSecondAbstractFile}
          existing={secondAbstractExisting}
          clearExisting={() => {
            setSecondAbstractExisting("");
            setSecondAbstractFile(null);
          }}
        />
        <UploadRow
          label="Video 1"
          inputId="video1-input"
          icon="🎥"
          accept="video/*"
          file={videoFile}
          setFile={setVideoFile}
          existing={videoExisting}
          clearExisting={() => {
            setVideoExisting("");
            setVideoFile(null);
          }}
        />
        <UploadRow
          label="Video 2"
          inputId="video2-input"
          icon="🎥"
          accept="video/*"
          file={secondVideoFile}
          setFile={setSecondVideoFile}
          existing={secondVideoExisting}
          clearExisting={() => {
            setSecondVideoExisting("");
            setSecondVideoFile(null);
          }}
        />
        <UploadRow
          label="Presentation 1"
          inputId="presentation1-input"
          icon="📁"
          accept=".pdf,.ppt,.pptx"
          file={presentationFile}
          setFile={setPresentationFile}
          existing={presentationExisting}
          clearExisting={() => {
            setPresentationExisting("");
            setPresentationFile(null);
          }}
        />
        <UploadRow
          label="Presentation 2"
          inputId="presentation2-input"
          icon="📁"
          accept=".pdf,.ppt,.pptx"
          file={secondPresentationFile}
          setFile={setSecondPresentationFile}
          existing={secondPresentationExisting}
          clearExisting={() => {
            setSecondPresentationExisting("");
            setSecondPresentationFile(null);
          }}
        />

        {/* Dates */}
        <div style={{ marginTop: 10 }}>
          <label style={labelStyle}>
            Arrival Date <span style={requiredStyle}>*</span>
          </label>
          <div style={rowWrap}>
            <input
              type="date"
              value={arrivalDate}
              onChange={(e) => setArrivalDate(e.target.value)}
              style={inputStyle}
              required
            />
          </div>
        </div>

        <div style={{ marginTop: 10 }}>
          <label style={labelStyle}>
            Departure Date <span style={requiredStyle}>*</span>
          </label>
          <div style={rowWrap}>
            <input
              type="date"
              value={departureDate}
              onChange={(e) => setDepartureDate(e.target.value)}
              style={inputStyle}
              required
            />
          </div>
        </div>

        {/* Topics */}
        <Topics />
        <div style={{ marginTop: 10 }}>
          <label style={labelStyle}>Topics</label>
          {topics.map((topic, i) => (
            <div key={i} style={{ display: "flex", marginBottom: 6 }}>
              <input
                value={topic}
                onChange={(e) => {
                  const newTopics = [...topics];
                  newTopics[i] = e.target.value;
                  setTopics(newTopics);
                }}
                style={{ ...inputStyle, flexGrow: 1 }}
                placeholder="Enter a topic"
              />
              <button
                type="button"
                onClick={() => setTopics(topics.filter((_, idx) => idx !== i))}
                style={removeTopicBtnStyle}
              >
                ×
              </button>
            </div>
          ))}
          <br />
          <button
            type="button"
            onClick={() => setTopics([...topics, ""])}
            style={addTopicBtnStyle}
          >
            + Add Topic
          </button>
        </div>

        {/* Additional flags */}
        <div style={additionalBoxStyle}>
          <strong>Additional</strong>
          <div>
            <label style={checkboxLabelStyle} title="Read-only">
              <input type="checkbox" checked={onlineApproved} readOnly />
              Online Approved
            </label>
            <label style={checkboxLabelStyle} title="Read-only">
              <input type="checkbox" checked={certificateActive} readOnly />
              Certificate Active
            </label>
            <label style={checkboxLabelStyle} title="Read-only">
              <input type="checkbox" checked={visaPaymentRequired} readOnly />
              Visa Fees Included
            </label>
          </div>
        </div>

        {/* Attendance */}
        {onlineApproved && (
          <div style={attendanceBoxStyle}>
            <strong>How would you like to attend the conference? </strong>
            <label style={radioLabelStyle}>
              <input
                type="radio"
                name="attendance"
                value="in-person"
                checked={onlineParticipation === 0}
                onChange={() => setOnlineParticipation(0)}
                required
              />
              In-Person
            </label>
            <label style={radioLabelStyle}>
              <input
                type="radio"
                name="attendance"
                value="online"
                checked={onlineParticipation === 1}
                onChange={() => setOnlineParticipation(1)}
                required
              />
              Online
            </label>
          </div>
        )}

        <button type="submit" disabled={isLoading} style={submitBtnStyle}>
          {isLoading ? "Updating..." : "Update Profile"}
        </button>
      </form>
    </div>
  );
};

// 🔥 ALL STYLES - COMPLETE FINAL VERSION
const containerStyle = {
  maxWidth: "700px",
  width: "100%",
  margin: "20px auto",
  backgroundColor: "white",
  padding: 25,
  borderRadius: 12,
  boxShadow: "0 0 15px rgba(163,0,0,0.3)",
  fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
};
const formStyle = { display: "flex", flexDirection: "column" };

// 🔥 PROFESSIONAL HEADER STYLES
const profileHeader = {
  position: "relative",
  borderRadius: 14,
  overflow: "hidden",
  border: "1px solid rgba(0,0,0,0.06)",
  boxShadow: "0 10px 30px rgba(17, 24, 39, 0.10)",
  marginBottom: 24,
};
const profileCover = {
  height: 74,
  background: "linear-gradient(135deg,#a30000,#6f0000)",
};
const profileHeaderInner = {
  display: "flex",
  gap: 16,
  padding: "18px 20px 20px",
  background: "#fff",
  alignItems: "flex-start",
};
const avatarWrap = { 
  position: "relative", 
  width: 100, 
  height: 100, 
  flex: "0 0 100px",
  marginTop: -20,
};
const avatarRing = {
  width: 100,
  height: 100,
  borderRadius: "50%",
  background: "conic-gradient(from 180deg, rgba(163,0,0,0.9), rgba(111,0,0,0.8), rgba(163,0,0,0.9))",
  padding: 4,
  boxShadow: "0 12px 24px rgba(163,0,0,0.20)",
  marginTop: "-10px"
};
const avatarImg = { 
  width: "100%", 
  height: "100%", 
  borderRadius: "50%", 
  objectFit: "cover", 
  background: "#fff", 
  
};
const avatarPlaceholder = {
  width: "100%",
  height: "100%",
  borderRadius: "50%",
  background: "#fff",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  color: "#9CA3AF",
  fontSize: 14,
  fontWeight: 500,
  border: "2px dashed rgba(163,0,0,0.3)",
};
const avatarEditBtn = {
  position: "absolute",
  right: 0,
  bottom: 0,
  width: 36,
  height: 36,
  borderRadius: "50%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
  background: "#a30000",
  color: "#fff",
  border: "3px solid #fff",
  boxShadow: "0 8px 20px rgba(163,0,0,0.25)",
  fontSize: 14,
  transition: "all 0.2s ease",
};
const profileText = { flex: 1, minWidth: 0 };

// 🔥 NEW TITLE DROPDOWN + NAME LAYOUT
const titleNameRow = {
  display: "flex",
  gap: 12,
  alignItems: "end",
  marginBottom: 10,
};
const titleDropdownWrap = {
  flex: "0 0 110px",
};
const titleDropdown = {
  width: "100%",
  border: "1px solid rgba(17,24,39,0.15)",
  borderRadius: 12,
  padding: "12px 14px",
  fontSize: 16,
  fontWeight: 600,
  outline: "none",
  background: "rgba(255,255,255,0.95)",
  boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
  appearance: "none",
  backgroundImage: `url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6,9 12,15 18,9'%3e%3c/polyline%3e%3c/svg%3e")`,
  backgroundRepeat: "no-repeat",
  backgroundPosition: "right 12px center",
  backgroundSize: "18px",
  cursor: "pointer",
  color: "#111827",
};
const nameInputWrap = {
  flex: 1,
};
const nameInput = {
  width: "100%",
  border: "1px solid rgba(17,24,39,0.15)",
  borderRadius: 12,
  padding: "12px 14px",
  fontSize: 18,
  fontWeight: 700,
  outline: "none",
  background: "rgba(255,255,255,0.95)",
  boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
  transition: "all 0.2s ease",
};
const helperLine = {
  fontSize: 13,
  color: "#6B7280",
  fontStyle: "italic",
};

// EXISTING STYLES (UNCHANGED)
const labelStyle = { fontWeight: "bold", marginBottom: 5 };
const rowWrap = { display: "flex", alignItems: "center", gap: 10 };
const growArea = { flexGrow: 1 };
const valueChip = {
  padding: "4px 8px",
  backgroundColor: "#f2f2f2",
  borderRadius: 6,
  cursor: "pointer",
  border: "1px solid #ccc",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};
const placeholderChip = {
  padding: "4px 8px",
  backgroundColor: "#f8f8f8",
  borderRadius: 6,
  border: "1px dashed #ccc",
  color: "#999",
};
const iconButton = {
  padding: "4px 8px",
  cursor: "pointer",
  backgroundColor: "#eee",
  borderRadius: 6,
  border: "1px solid #ccc",
};
const clearBtn = {
  padding: "4px 8px",
  cursor: "pointer",
  border: "none",
  backgroundColor: "#f44336",
  color: "white",
  borderRadius: 4,
};
const inputStyle = { width: "100%", padding: 8, borderRadius: 6, border: "1px solid #ccc" };
const textareaStyle = {
  width: "100%",
  padding: 8,
  borderRadius: 6,
  border: "1px solid #ccc",
  minHeight: 80,
  resize: "vertical",
};
const requiredStyle = { color: "red" };
const additionalBoxStyle = { marginTop: 20, padding: 12, border: "1px solid #ccc", borderRadius: 6 };
const checkboxLabelStyle = { marginRight: 10, display: "inline-flex", alignItems: "center", gap: 4 };
const attendanceBoxStyle = { marginTop: 20, padding: 12, border: "1px solid #ccc", borderRadius: 6 };
const radioLabelStyle = { marginRight: 10 };
const submitBtnStyle = {
  marginTop: 25,
  padding: "12px 24px",
  border: "none",
  borderRadius: 8,
  backgroundColor: "#a30000",
  color: "white",
  cursor: "pointer",
  fontSize: 16,
  fontWeight: "bold",
};
const removeTopicBtnStyle = {
  marginLeft: 6,
  background: "red",
  color: "white",
  border: "none",
  borderRadius: 4,
  padding: "2px 6px",
  cursor: "pointer",
};
const addTopicBtnStyle = {
  marginTop: 6,
  background: "#a30000",
  color: "white",
  border: "none",
  borderRadius: 6,
  padding: "6px 12px",
  cursor: "pointer",
};

export default SpeakerProfileForm;
